import { useCallback, useRef } from 'react';
import { V2ApiError, V2NoResponseError } from '../services/v2/errors';

/** A random v4 UUID: a key only has to be unique among one user's requests. */
export const newIdempotencyKey = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

/**
 * One attempt of a money request: an Idempotency-Key and the exact body sent
 * with it. When the answer is lost — the server may have done the work after
 * the app stopped waiting — pressing again resends the same body with the same
 * key, and the server returns the first answer instead of filing a second
 * request and locking the amount twice.
 *
 * `stable` names what makes it the same request (amount, account, address…);
 * fields that drift on their own, such as a live rate, are left out of it, so
 * a retry keeps the body that was first sent rather than failing as a
 * different request under the same key. Any change to `stable` starts a new
 * attempt; so does the next request after an answer, refusals included —
 * only a lost answer, the server saying the first is still running, or a 5xx
 * keeps the attempt. A 5xx may come from the gateway while the server still
 * finished the work; keeping the key is safe either way, as a server that
 * failed the request dropped the key and runs the repeat anew.
 *
 *   const idempotency = useIdempotencyKey<V2CreateTransferRequest>();
 *   const attempt = idempotency.attempt(request);
 *   try {
 *     await v2CreateTransfer(attempt.request, attempt.key);
 *     idempotency.answered();
 *   } catch (error) {
 *     idempotency.failed(error);
 *   }
 */
export const useIdempotencyKey = <T>() => {
  const current = useRef<{ fingerprint: string; key: string; request: T } | null>(null);

  const attempt = useCallback((request: T, stable: unknown = request): { key: string; request: T } => {
    const fingerprint = JSON.stringify(stable ?? null);
    if (current.current?.fingerprint !== fingerprint) {
      current.current = { fingerprint, key: newIdempotencyKey(), request };
    }
    return { key: current.current.key, request: current.current.request };
  }, []);

  const answered = useCallback(() => {
    current.current = null;
  }, []);

  const failed = useCallback((error: unknown) => {
    const answerLost = error instanceof V2NoResponseError;
    const stillRunning = error instanceof V2ApiError && error.code === 'request_in_progress';
    // 502 / 503 / 504 from the gateway say nothing about whether the server
    // filed the request; its own 500 left no key behind. Keep the attempt.
    const serverSide = error instanceof V2ApiError && error.status >= 500;
    if (!answerLost && !stillRunning && !serverSide) {
      current.current = null;
    }
  }, []);

  return { attempt, answered, failed };
};
